import React, { useMemo } from 'react';
import styled from 'styled-components';
import YouTubeBigThumbnail from './YouTubeBigThumbnail';

const VideoWrapper = styled.div<{ $maxWidth: number | undefined }>`
  display: flex;
  justify-content: center;
`;

const StyledIframe = styled.iframe<{
  $maxWidth: number | undefined;
  $aspectRatio: string;
}>`
  border: none;
  ${({ $maxWidth }) => $maxWidth && `max-width: ${$maxWidth}px;`}
  aspect-ratio: ${({ $aspectRatio }) => $aspectRatio};
`;

type Props = {
  videoId: string;
  altText?: string;
  autoplay?: boolean;
  linkParameters?: string;
  width?: number;
  aspectRatio?: string;
};

function YouTubePlayer({
  videoId,
  autoplay = false,
  altText,
  linkParameters,
  width,
  aspectRatio = '16 / 9'
}: Props) {
  const paramsString = useMemo(() => {
    const params = new URLSearchParams(linkParameters);

    params.set('enablejsapi', '1');
    params.set('rel', '0');

    if (!params.has('autoplay')) {
      params.set('autoplay', autoplay ? '1' : '0');
    }

    return params.toString();
  }, [linkParameters, autoplay]);

  return (
    <VideoWrapper $maxWidth={width}>
      <StyledIframe
        title={altText}
        width="100%"
        src={`https://www.youtube.com/embed/${videoId}?${paramsString}`}
        allow="autoplay; encrypted-media"
        data-testid={'youtube-player'}
        $maxWidth={width}
        $aspectRatio={aspectRatio}
      />
    </VideoWrapper>
  );
}

export const YouTubeMedia = ({
  playVideo,
  currentVideoId,
  videoId,
  thumbnail,
  altText
}: {
  playVideo: boolean;
  currentVideoId: string;
  videoId: string;
  thumbnail?: string;
  altText: string;
}) => {
  if (!thumbnail) {
    return <YouTubePlayer videoId={videoId} altText={altText} />;
  }

  if (playVideo && currentVideoId === videoId) {
    return (
      <YouTubePlayer
        videoId={videoId}
        autoplay={true}
        altText={altText}
        aspectRatio="1.6"
      />
    );
  }

  return <YouTubeBigThumbnail thumbnailSrc={thumbnail} altText={altText} />;
};

export default YouTubePlayer;
